// ============================================================================
// Wissensdatenbank — Komponenten / Tarife / Förderungen / Netzbetreiber
// ----------------------------------------------------------------------------
// Datenquellen (bewusst getrennt, hier dokumentiert):
//   - Komponenten: Component-Tabelle via GET /knowledge/components — DIESELBE
//     Quelle wie der Komponenten-Selector der Elektroplanung (Stringing).
//   - Tarife/Förderungen/Netzbetreiber: kuratierte Referenzdaten der API
//     (knowledge.data.ts) — Richtwerte mit Pflege-Stand, keine Live-Quelle.
//   - Der PV-Kosten-Katalog (modules/costing) ist ein SEPARATER
//     Kalkulationskatalog (Stage 1 statisch) und wird hier nicht angezeigt.
// Ehrlichkeit: Sample-Daten heißen MUSTER, nichts wird als geprüft/freigegeben
// dargestellt, leere Kategorien sagen klar "noch nicht gepflegt".
// ============================================================================
import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api-client.js";
import { eur } from "@/lib/format.js";
import { EmptyState } from "@/components/ui/EmptyState.js";
import {
  unwrapList,
  componentTypeLabel,
  keySpecsFor,
  qualityBadgeFor,
  provenanceFor,
  emptyTextFor,
} from "./knowledge-helpers.js";
import type {
  KnowledgeComponent,
  KnowledgeTariff,
  KnowledgeSubsidy,
  KnowledgeGridOperator,
} from "./knowledge-helpers.js";

const FILTER_BUTTONS = [
  { label: "Alle", value: "" },
  { label: "PV-Module", value: "pv_module" },
  { label: "Wechselrichter", value: "inverter" },
  { label: "Speicher", value: "battery" },
  { label: "Wallbox", value: "wallbox" },
];

type Tab = "components" | "tariffs" | "subsidies" | "gridOperators";

const TABS: { id: Tab; label: string }[] = [
  { id: "components", label: "Komponenten" },
  { id: "tariffs", label: "Tarife" },
  { id: "subsidies", label: "Förderungen" },
  { id: "gridOperators", label: "Netzbetreiber" },
];

const TONE_STYLES: Record<string, React.CSSProperties> = {
  ok: { background: "#f0fdf4", color: "#15803d", border: "1px solid #86efac" },
  warn: { background: "#fffbeb", color: "#b45309", border: "1px solid #fcd34d" },
  muted: { background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" },
};

// ── Komponenten-Tab ───────────────────────────────────────────────────────────

function ComponentsTab() {
  const [components, setComponents] = useState<KnowledgeComponent[]>([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const path = "/knowledge/components" + (typeFilter ? `?type=${typeFilter}` : "");
    void apiClient
      .get<unknown>(path)
      .then((res) => {
        setComponents(res.success ? unwrapList<KnowledgeComponent>(res.data) : []);
      })
      .catch(() => setComponents([]))
      .finally(() => setLoading(false));
  }, [typeFilter]);

  const hasSampleData = components.some((c) => c.dataQuality === "sample");

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {FILTER_BUTTONS.map((btn) => (
          <button
            key={btn.value}
            onClick={() => setTypeFilter(btn.value)}
            style={{
              padding: "6px 14px", borderRadius: 6,
              border: "1px solid var(--color-border)",
              background: typeFilter === btn.value ? "var(--color-primary)" : "#fff",
              color: typeFilter === btn.value ? "#fff" : "var(--color-text)",
              cursor: "pointer", fontSize: 13,
              fontWeight: typeFilter === btn.value ? 600 : 400,
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {hasSampleData && (
        <div
          data-testid="sample-warning"
          style={{ background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 6, padding: "8px 12px", fontSize: 12, color: "#92400e", marginBottom: 12 }}
        >
          ⚠ <strong>MUSTERDATEN</strong> — Einträge mit MUSTER-Kennzeichnung sind Demo-/Beispielwerte.
          Vor Verwendung Datenblatt prüfen. Keine Hersteller-/TOR-Freigabe.
        </div>
      )}

      {loading && (
        <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>Lade Komponenten…</div>
      )}

      {!loading && components.length === 0 && (
        <div data-testid="components-empty">
          <EmptyState title="Keine Komponenten" description={emptyTextFor(typeFilter)} />
        </div>
      )}

      {!loading && components.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table data-testid="components-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Hersteller</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Modell</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Typ</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Kennwerte</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Qualität</th>
                <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: 600 }}>Quelle</th>
              </tr>
            </thead>
            <tbody>
              {components.map((c) => {
                const badge = qualityBadgeFor(c);
                return (
                  <tr key={c.id} style={{ borderBottom: "1px solid var(--color-border)" }}>
                    <td style={{ padding: "8px 12px" }}>{c.manufacturer}</td>
                    <td style={{ padding: "8px 12px" }}>{c.modelName}</td>
                    <td style={{ padding: "8px 12px" }}>{componentTypeLabel(c.type)}</td>
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{keySpecsFor(c.type, c.specs)}</td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, ...TONE_STYLES[badge.tone] }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: "8px 12px", fontSize: 11, color: "var(--color-text-muted)" }}>
                      {provenanceFor(c)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tarife-Tab (kuratierte Richtwerte) ───────────────────────────────────────

function TariffsTab() {
  const [tariffs, setTariffs] = useState<KnowledgeTariff[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    void apiClient
      .get<unknown>("/knowledge/tariffs")
      .then((res) => setTariffs(res.success ? unwrapList<KnowledgeTariff>(res.data) : []))
      .catch(() => setTariffs([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>Lade Tarife…</div>;
  if (tariffs.length === 0) {
    return (
      <div style={{ color: "var(--color-text-muted)", fontSize: 13, textAlign: "center", padding: 32 }}>
        Noch keine Tarife gepflegt — Richtwerte folgen.
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10 }}>
        Kuratierte Richtwerte (netto) — kein Live-Tarifvergleich, vor Kalkulation aktuelle Tarife prüfen.
      </div>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}>
        {tariffs.map((t) => (
          <div key={t.id} className="card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t.name}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "var(--color-primary)", marginBottom: 4 }}>
              {typeof (t.totalCtPerKwh ?? t.priceCtPerKwh) === "number"
                ? `${(t.totalCtPerKwh ?? t.priceCtPerKwh).toLocaleString("de-AT")} ct/kWh`
                : "— (Preis nicht gepflegt)"}
            </div>
            {typeof t.totalCtPerKwh === "number" && typeof t.gridFeeCtPerKwh === "number" && (
              <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
                Energie {t.priceCtPerKwh} + Netz {t.gridFeeCtPerKwh} + Abgaben {t.taxesCtPerKwh ?? 0}
              </div>
            )}
            {t.note && <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 6 }}>{t.note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Förderungen-Tab ───────────────────────────────────────────────────────────

function SubsidiesTab() {
  const [subsidies, setSubsidies] = useState<KnowledgeSubsidy[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    void apiClient
      .get<unknown>("/knowledge/subsidies")
      .then((res) => setSubsidies(res.success ? unwrapList<KnowledgeSubsidy>(res.data) : []))
      .catch(() => setSubsidies([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>Lade Förderungen…</div>;
  if (subsidies.length === 0) {
    return (
      <div style={{ color: "var(--color-text-muted)", fontSize: 13, textAlign: "center", padding: 32 }}>
        Noch keine Förder-Referenzen gepflegt — die vollständige Übersicht liegt unter „Förderungen" in der Sidebar.
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10 }}>
        Referenzwerte ohne Gewähr — Förderhöhen/Fristen vor Antrag prüfen. Keine Förder-Garantie.
      </div>
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}>
        {subsidies.map((s) => (
          <div key={s.id} className="card" style={{ padding: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{s.name}</div>
            <div style={{ display: "flex", gap: 16, marginBottom: 6 }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 2 }}>Max. Förderung</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--color-primary)" }}>
                  {typeof s.maxEur === "number" ? eur(s.maxEur) : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 2 }}>Anteil</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{s.percentOfInvestment} %</div>
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>Region: {s.region}{s.eligibility ? ` · ${s.eligibility}` : ""}</div>
            {s.validUntil && (
              <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4 }}>Laufzeit bis: {s.validUntil}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Netzbetreiber-Tab ─────────────────────────────────────────────────────────

function GridOperatorsTab() {
  const [operators, setOperators] = useState<KnowledgeGridOperator[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    void apiClient
      .get<unknown>("/knowledge/grid-operators")
      .then((res) => setOperators(res.success ? unwrapList<KnowledgeGridOperator>(res.data) : []))
      .catch(() => setOperators([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: "var(--color-text-muted)", fontSize: 13 }}>Lade Netzbetreiber…</div>;
  if (operators.length === 0) {
    return (
      <div style={{ color: "var(--color-text-muted)", fontSize: 13, textAlign: "center", padding: 32 }}>
        Noch keine Netzbetreiber gepflegt.
      </div>
    );
  }

  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
      {operators.map((op) => (
        <li
          key={op.name}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 16px", borderRadius: 8,
            border: "1px solid var(--color-border)", background: "#fff",
          }}
        >
          <span style={{ fontWeight: 500 }}>{op.name}</span>
          <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
            {op.region}{op.website ? ` · ${op.website}` : ""}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── Haupt-Seite ───────────────────────────────────────────────────────────────

export function KnowledgePage() {
  const [activeTab, setActiveTab] = useState<Tab>("components");

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Wissensdatenbank</h1>
      </div>

      <div style={{ display: "flex", gap: 0, marginBottom: 24, borderBottom: "2px solid var(--color-border)" }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none",
              borderBottom: activeTab === tab.id ? "2px solid var(--color-primary)" : "2px solid transparent",
              marginBottom: -2, background: "transparent",
              color: activeTab === tab.id ? "var(--color-primary)" : "var(--color-text-muted)",
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: 14, cursor: "pointer",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="card">
        {activeTab === "components" && <ComponentsTab />}
        {activeTab === "tariffs" && <TariffsTab />}
        {activeTab === "subsidies" && <SubsidiesTab />}
        {activeTab === "gridOperators" && <GridOperatorsTab />}
      </div>
    </div>
  );
}
